/**
 * App.tsx — the launch, and the four things a person can see because of it.
 *
 * Task 04. Mounting starts exactly one request: `POST /api/auth/telegram` with the launch
 * data Telegram put in the page. Everything else on screen is a consequence of its answer.
 *
 * | состояние | что видно |
 * |---|---|
 * | `loading` | название продукта, пока идёт запрос |
 * | `ready` | оболочка с четырьмя вкладками |
 * | `not-subscribed` | экран «подпишитесь» — [[SubscribeGate]] |
 * | `failed` | одна фраза и кнопка «Ещё раз» |
 *
 * ## Why the request starts here and not in `mount.tsx`
 *
 * `mount.tsx` is the DOM boundary and has no business knowing there is a server. Putting
 * the launch in an effect also means React's `StrictMode` double-invoke exercises the
 * «called twice» path in development — and «Я подписался» is that same call, so it is a path
 * that has to be safe anyway.
 *
 * ## `startapp=proba` is not the app (task 42)
 *
 * A launch with that payload leaves for the probe of two decks (`app/probe-door.ts`) the moment
 * the sign-in answers — the shell is never drawn, so no queue is read and no sync starts. Task 53
 * added `startapp=probapult`, the probe of the pult, the same way, and task 74
 * `startapp=probavideo`, the probe of clips in a row.
 *
 * ## Telegram is told the page is ready before the request, not after
 *
 * `telegram.ready()` paints the client's own chrome in our ground colour and expands the
 * window. Waiting for the server would leave a light-coloured strip above a dark page for as
 * long as the network takes, which is exactly the frame a person judges the product on.
 */

import type { JSX } from 'react';
import { useEffect } from 'react';

import { Shell } from './app/Shell';
import { isProbeLaunch, probeAddress } from './app/probe-door';
import { SubscribeGate } from './app/SubscribeGate';
import { player } from './engine/player';
import { telegram } from './lib/telegram';
import { useSession } from './store/session';
import './styles/app.css';

/** The interface name, fixed by the build contract. */
export const APP_TITLE = 'Flash Dancehall Music';

/** Shown while the first request is in flight. */
export const LOADING_TEXT = 'Открываю плеер…';

/** Task 42: shown for the moment the page takes to leave for the probe. */
export const PROBE_TEXT = 'Открываю пробу…';

/** The label on the only control an error screen has. */
export const RETRY_LABEL = 'Ещё раз';

/**
 * The ground colour, as a literal.
 *
 * Telegram's chrome cannot be painted from a CSS custom property — the client wants a hex
 * string — so this one value exists in two places. `apps/web/test/theme.test.ts` asserts the
 * two agree, which is cheaper than a computed-style read that returns an empty string in
 * every non-browser environment.
 */
export const GROUND = '#0b0a09';

/** Task 42: hands the page over to the probe the launch names, keeping the launch data. */
function ProbeDoor({ startParam }: { readonly startParam: string }): JSX.Element {
  useEffect(() => {
    window.location.replace(probeAddress(window.location.hash, startParam));
  }, [startParam]);
  return (
    <section className="state">
      <h1 className="stateTitle">{APP_TITLE}</h1>
      <p className="stateText">{PROBE_TEXT}</p>
    </section>
  );
}

export function App(): JSX.Element {
  const status = useSession((s) => s.status);
  const failure = useSession((s) => s.failure);
  const joinUrl = useSession((s) => s.joinUrl);
  const signIn = useSession((s) => s.signIn);
  const probe = useSession((s) =>
    s.status === 'ready' && isProbeLaunch(s.session?.startParam)
      ? s.session?.startParam
      : undefined,
  );

  useEffect(() => {
    telegram.ready();
    telegram.paintChrome(GROUND);

    // The client's stable viewport height, when it says one. Inside Telegram even `dvh`
    // counts the strip under Telegram's own header, so the page would be that much too tall.
    // Kept current, not read once: the frame is exactly this tall and the tabs sit at its
    // bottom edge, so a height from before the window expanded would put them mid-screen.
    const applyHeight = (height: number): void => {
      document.documentElement.style.setProperty('--app-h', `${String(height)}px`);
    };
    const height = telegram.stableHeight();
    if (height !== undefined) applyHeight(height);
    const stopWatching = telegram.onStableHeightChange(applyHeight);

    // Task 07. Closing the Mini App stops the music on both phones and nothing can prevent
    // it, so a stray swipe is asked about first (`spec.md` «Плеер»).
    telegram.enableClosingConfirmation();
    // Minimised and back: the state is handed to the server (task 08) and read back, and the
    // sound is left alone in both directions — the handlers never call `play()` or `pause()`.
    const stopLifecycle = telegram.onActiveChange((active) => {
      if (active) player().resync();
      else player().save();
    });

    void signIn(telegram.initData);
    return () => {
      stopWatching();
      stopLifecycle();
    };
  }, [signIn]);

  if (probe !== undefined && probe !== null) return <ProbeDoor startParam={probe} />;
  if (status === 'ready') return <Shell />;

  if (status === 'not-subscribed') {
    return <SubscribeGate joinUrl={joinUrl} onRetry={() => signIn(telegram.initData)} />;
  }

  if (status === 'failed') {
    return (
      <section className="state">
        <h1 className="stateTitle">{APP_TITLE}</h1>
        <p className="stateText">{failure?.message ?? 'Что-то пошло не так.'}</p>
        <div className="actions">
          <button
            type="button"
            className="button"
            onClick={() => {
              void signIn(telegram.initData);
            }}
          >
            {RETRY_LABEL}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="state">
      <h1 className="stateTitle">{APP_TITLE}</h1>
      <p className="stateText">{LOADING_TEXT}</p>
    </section>
  );
}
